import React, { FC, ReactNode, useCallback } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import {
  isPublicProjectPathname,
  NODON_GENERATION_PROJECT_MATCH_PATH,
  NODON_SHARED_PROJECT_MATCH_PATH,
} from '@shared/constants/router.constants';
import { AppState } from '@auth0/auth0-react/src/auth0-provider';
import { useConfig } from './ConfigProvider';
import { AuthDisabledProvider, isAuthDisabled } from './AuthDisabledProvider';
import {
  getAuth0CacheLocation,
  shouldUseAuth0RefreshTokenFallback,
} from './auth0-session.helpers';
import { Auth0LocalhostRopgGate } from './Auth0LocalhostRopgProvider';
import {
  isLocalhostRopgSkipped,
  shouldAttemptLocalhostRopg,
} from '../helpers/auth0-localhost-ropg.helpers';

type Auth0ProviderWithHistoryProps = {
  children: ReactNode;
};

const Auth0ProviderWithHistory: FC<Auth0ProviderWithHistoryProps> = ({
  children,
}) => {
  const [config] = useConfig();
  const navigate = useNavigate();
  const domain = config.auth0.domain;
  const clientId = config.auth0.clientId;
  const audience = config.auth0.audience;

  const { pathname } = useLocation();
  const sharedMatch = useMatch(NODON_SHARED_PROJECT_MATCH_PATH);
  const generationMatch = useMatch(NODON_GENERATION_PROJECT_MATCH_PATH);
  const isPublicProjectRoute =
    isPublicProjectPathname(pathname) ||
    Boolean(sharedMatch || generationMatch);

  const onRedirectCallback = useCallback(
    (appState?: AppState): void => {
      const returnTo = appState?.returnTo || window.location.pathname;
      navigate(returnTo);
    },
    [navigate],
  );

  // e2e tests run without Auth0 — provide a permanently authenticated context
  if (isAuthDisabled()) {
    return <AuthDisabledProvider>{children}</AuthDisabledProvider>;
  }

  if (isPublicProjectRoute) {
    return <>{children}</>;
  }

  const auth0Provider = (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{ redirect_uri: window.location.origin, audience }}
      onRedirectCallback={onRedirectCallback}
      // Localhost: persist tokens across HMR remounts. Hosted apps stay on memory.
      cacheLocation={getAuth0CacheLocation(window.location.hostname)}
      useRefreshTokens={true}
      useRefreshTokensFallback={shouldUseAuth0RefreshTokenFallback(
        window.location.hostname,
      )}
    >
      {children}
    </Auth0Provider>
  );

  if (
    shouldAttemptLocalhostRopg({
      hostname: window.location.hostname,
      isDev: import.meta.env.DEV,
      skip: isLocalhostRopgSkipped(),
    })
  ) {
    return (
      <Auth0LocalhostRopgGate apiUrl={config.apiUrl} fallback={auth0Provider}>
        {children}
      </Auth0LocalhostRopgGate>
    );
  }

  return auth0Provider;
};

export default Auth0ProviderWithHistory;
